import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Icon from 'icons/Icon'
import { filterProvidersBySearch, getAllowedProviderStudios } from 'utils/provider'
import './styles/index.scss'

/**
 * A component that renders a filterable list of game providers and their associated studios.
 * Allows users to search, select/deselect providers and individual studios.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.selectedFilter - Currently selected filters state object
 * @param {Array<string>} props.selectedFilter.provider - Array of selected provider names
 * @param {Array<string>} props.selectedFilter.studio - Array of selected studio IDs
 * @param {Function} props.setSelectedFilter - Callback to update selected filters
 * @returns {JSX.Element} Rendered game provider filters component
 */
const GameProviderFilters = ({
  selectedFilter,
  setSelectedFilter
}) => {
  const { providers = [], studios } = useSelector((state) => state.casinoMenu)
  const { languageData } = useSelector((state) => state.language)

  // Allowed studios by ID (country/category scoped)
  const allowedStudioIdSet = useMemo(() => new Set((studios || []).map(s => s?.id).filter(id => id != null)), [studios])

  // UI expand/collapse state by provider tenantCasinoProviderId
  const [expanded, setExpanded] = useState({})
  const toggleExpand = (pid) => setExpanded(prev => ({ ...prev, [pid]: !prev[pid] }))

  // Search state
  const [query, setQuery] = useState('')
  const searchQuery = (query || '').trim().toLowerCase()

  const getProviderStudios = (provider) => getAllowedProviderStudios(provider, allowedStudioIdSet)

  const filteredProviders = useMemo(
    () => filterProvidersBySearch(providers, searchQuery, allowedStudioIdSet),
    [providers, searchQuery, allowedStudioIdSet]
  )

  const isProviderSelected = (name) => selectedFilter?.provider?.includes(name)
  const isStudioSelected = (id) => selectedFilter?.studio?.includes(id)

  const selectProvider = (providerName, providerStudios) => {
    // Add a provider name and remove all its studios from a studio list
    setSelectedFilter((selectedFilter) => ({
      ...selectedFilter,
      provider: [...(selectedFilter?.provider || []), providerName],
      studio: (selectedFilter?.studio || []).filter(id => !providerStudios.some(studio => studio?.id === id))
    }))
  }

  const toggleProvider = (provider) => {
    const providerName = provider?.name
    const providerStudios = getProviderStudios(provider)
    if (isProviderSelected(providerName)) {
      // Unselect the provider and also ensure its studios are removed from studio selection
      setSelectedFilter((selectedFilter) => ({
        ...selectedFilter,
        provider: (selectedFilter?.provider || []).filter(n => n !== providerName),
        studio: (selectedFilter?.studio || []).filter(id => !providerStudios.some(studio => studio?.id === id))
      }))
    } else {
      selectProvider(providerName, providerStudios)
    }
  }

  const toggleStudio = (studio, provider) => {
    const studioId = studio?.id
    const providerName = provider?.name

    setSelectedFilter((selectedFilter) => {
      const currentStudios = new Set(selectedFilter?.studio || [])
      const currentProviders = new Set(selectedFilter?.provider || [])

      const allowedStudios = getProviderStudios(provider)
      const allowedIds = allowedStudios.map(s => s?.id).filter(id => id != null)

      if (currentProviders.has(providerName)) {
        // Demote from provider to studio selection: select all studios except the clicked one
        currentProviders.delete(providerName)
        allowedIds.forEach(id => { if (id !== studioId) currentStudios.add(id) })
        // Ensure the clicked studio is not selected
        currentStudios.delete(studioId)
      } else {
        // Normal toggle behavior when a provider is not fully selected
        if (currentStudios.has(studioId)) {
          currentStudios.delete(studioId)
        } else {
          currentStudios.add(studioId)
        }

        // Promotion: if all allowed studios for this provider are now selected → select provider and clear those studios
        const allSelected = allowedIds.length > 0 && allowedIds.every(id => currentStudios.has(id))
        if (allSelected) {
          currentProviders.add(providerName)
          allowedIds.forEach(id => currentStudios.delete(id))
        }
      }

      return {
        ...selectedFilter,
        provider: Array.from(currentProviders),
        studio: Array.from(currentStudios)
      }
    })
  }

  const getProviderState = (p) => {
    const name = p?.name
    if (isProviderSelected(name)) return 'all'
    const studios = getProviderStudios(p)
    if (!studios.length) return 'none'
    const selectedCount = studios.reduce((acc, s) => acc + (isStudioSelected(s?.id) ? 1 : 0), 0)
    if (selectedCount === 0) return 'none'
    if (selectedCount === studios.length) return 'all'
    return 'partial'
  }

  return (
    <div className='game-provider-filters'>
      <div className='provider-search'>
        <div className='provider-search-input'>
          <input
            type='text'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={languageData?.searchProvidersStudios || 'Search providers & studios'}
          />
          {searchQuery
            ? (
              <button
                type='button'
                className='clear-btn'
                onClick={() => setQuery('')}
                aria-label='Clear'
              >
                <Icon name='xmark' width='14' height='14' />
              </button>
              )
            : (
              <Icon name='search' width='16' height='16' />
              )}
        </div>
      </div>
      <div>
        {searchQuery && filteredProviders?.length === 0 && (
          <p className='no-search-results'>{languageData?.noSearchResultsFound}</p>
        )}
        {/* TODO: Extract in a function */}
        {filteredProviders?.map((provider) => {
          const providerId = provider?.tenantCasinoProviderId || provider?.masterCasinoProviderId || provider?.name
          const state = getProviderState(provider)
          const checked = state === 'all'
          const partial = state === 'partial'
          const allStudios = getProviderStudios(provider)
          let studiosToRender = allStudios
          if (searchQuery) {
            const matched = Array.isArray(provider?.__matchedStudios) ? provider.__matchedStudios : []
            studiosToRender = matched.length ? matched : allStudios
          }
          const canExpand = (allStudios?.length || 0) > 0
          const isExpanded = searchQuery ? canExpand : !!expanded[providerId]

          return (
            <div className='accordion accordion-flush' key={`provider-accordion-${providerId}`}>
              <div className='accordion-item provider-accordion'>
                <div className='accordion-header'>
                  <div
                    className={`provider-checklist-item  ${checked ? 'selected' : ''} ${partial ? 'partial' : ''}`}
                    onClick={(e) => {
                      if (!canExpand) return
                      e.preventDefault()
                      e.stopPropagation()
                      toggleExpand(providerId)
                    }}
                    role='button'
                    aria-expanded={isExpanded}
                  >
                    <div className='provider'>
                      <span
                        className='provider-checkbox'
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleProvider(provider)
                        }}
                        role='checkbox'
                        aria-checked={checked ? 'true' : (partial ? 'mixed' : 'false')}
                      >
                        <Icon name={checked ? 'casino-filter-checked' : (partial ? 'casino-filter-unchecked' : 'casino-filter-unchecked')} className='mr-2' width='20px' height='20px' />
                      </span>
                      <span className='provider-name'>{provider?.providerDisplayName || provider?.name}</span>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                      <span className='studio-count'>{(allStudios?.length || 0)} {languageData?.studios || 'studios'}</span>
                      <button
                        type='button'
                        className={`accordion-button toggle-btn ${canExpand ? 'visible' : 'invisible'} ${isExpanded ? '' : 'collapsed'}`}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleExpand(providerId)
                        }}
                        aria-expanded={isExpanded}
                      >
                        <Icon
                          name={isExpanded ? 'chevron-up' : 'chevron-down'}
                          height={16}
                          width={16}
                          fill='white'
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}>
                  <div className='accordion-body'>
                    {studiosToRender?.length > 0 && studiosToRender.map(studio => {
                      const { GameStudio, id } = studio
                      const isStudioChecked = isProviderSelected(provider?.name) || isStudioSelected(id)
                      return (
                        <span
                          key={`studio-${providerId}-${id}`}
                          className={`studio-checklist-item ${isStudioChecked ? 'selected' : ''}`}
                          onClick={() => toggleStudio(studio, provider)}
                        >
                          <Icon name={isStudioChecked ? 'casino-filter-checked' : 'casino-filter-unchecked'} className='mr-2' width='20px' height='20px' />
                          <span className='studio-name'>{GameStudio?.displayName || GameStudio?.name}</span>
                        </span>
                      )
                    })}
                    {studiosToRender?.length === 0 && (
                      <div className='no-studios text-muted py-2'>
                        {languageData?.noStudios || 'No Studios'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default GameProviderFilters
